/**
 * narcisTheme — uniform 2:3, no ratio picker, no `visitor` policy (an anonymous reader sees
 * the wall as a member; the wall reads them as having no model, `chains.wallIdentity`). The pencil door stands only once a model is
 * ready (operator, 2026-09-22: "queuing and training buttons are enough").
 */
import {RATIO_KEYS} from '@core/wall/contract'
import type {HouseTheme} from '@core/wall/ui/theme'
import {PHOTO_VERB, WALL_ADD_PHOTOS_VERB, WALL_PHOTO_ASKED} from '@product/narcis/proposition'
import {IdentityPanel} from './IdentityPanel'
import {NARCIS_FACETS} from './facets'
import {previewImageUrl, takeImageUrl, tileImageUrl} from './urls'

export const narcisTheme: HouseTheme = {
    id: 'narcis',
    facets: NARCIS_FACETS,
    // Six columns of 2:3 at 1440: (1440 - 5 * 8) / 6 / (832 / 1216) ≈ 339.
    targetRowHeight: 339,
    gap: 8,
    // Ovid's value: portrait houses take a narrower bar (plan `2026_09_18_armed_frame_budget.md` § D5).
    armed: {barWidth: 520},
    hasRatioPicker: false,
    ratioKeys: RATIO_KEYS,
    defaultRatio: '2:3',
    pencil: true,
    pencilWhen: (identity) => identity?.kind === 'model' && identity.state === 'ready',
    identityPanel: IdentityPanel,
    verbs: {
        generate: 'Generate',
        again: 'Again',
        placeholder: {onPicture: 'What should change?', onBlank: 'What do you want to see?'},
        blankBasisLabel: 'blank canvas',
        refused: 'not something we draw',
        model: {missing: WALL_ADD_PHOTOS_VERB, missingPack: WALL_ADD_PHOTOS_VERB, training: PHOTO_VERB, queued: WALL_PHOTO_ASKED},
    },
    tileImageUrl,
    takeImageUrl,
    previewImageUrl,
    tileLabelHref: (packSlug: string) => `/${packSlug}`,
    share: {
        link: true,
        fileBase: 'narcis',
        words: {
            door: 'Share',
            native: 'Share…',
            download: 'Download',
            copyImage: 'Copy picture',
            copyLink: 'Copy link',
            title: 'Made with Narcis',
            text: 'Made with Narcis — make yours: {url}',
        },
    },
}

/**
 * narcisTheme with the scheduled packs' tile labels left as plain text: their pages 404 until their
 * release day (thinking/plans/2026_09_28_pack_release_schedule.md, decision 4), while their tiles
 * are on the wall from ship time. The slugs come from the home loader — the release dates live in
 * the server-side catalog, which this client module does not import.
 */
export function narcisThemeFor(unreleasedSlugs: readonly string[]): HouseTheme {
    if (unreleasedSlugs.length === 0) return narcisTheme
    const unreleased = new Set(unreleasedSlugs)
    return {
        ...narcisTheme,
        tileLabelHref: (packSlug: string) => (unreleased.has(packSlug) ? null : narcisTheme.tileLabelHref(packSlug)),
    }
}
